import { format } from 'date-fns';
import {
  ConditionalEventsFiltersState,
  ConditionalEventsFilters,
  FormattedTransportConditionalEventsReportItemDTO,
  TransportConditionalEventsReportItemDTO,
  ConditionalEventType,
} from '../types';
import { getTimezone } from '../../../../global/utils';
import { formatReportPayloadDate } from '../../../utils/helpers';
import { getDuration } from '../../../../shared/utils';
import { EVENT_TYPE_FILTERS } from './constants';

export const formatConditionalEventsReportItem = (
  transportId: number,
  reportItem: TransportConditionalEventsReportItemDTO,
  transportName: string,
  licensePlate: string,
): FormattedTransportConditionalEventsReportItemDTO => {
  return {
    ...reportItem,
    transportId,
    transportName,
    licensePlate,
    id: crypto.randomUUID(),
    durationFormmated: reportItem.duration
      ? getDuration(reportItem.duration)
      : null,
    dateFormatted: formatEventDate(reportItem.start),
    startEndTimeFormatted: formatEventStartEndTime(
      reportItem.start,
      reportItem.end,
    ),
  };
};

export const formatConditionalEventsPayload = (
  filters: ConditionalEventsFiltersState,
): ConditionalEventsFilters | null => {
  const eventTypeConfig =
    EVENT_TYPE_FILTERS[filters.eventType as ConditionalEventType];

  if (!eventTypeConfig) {
    return null;
  }

  let eventTypeFilters: Pick<
    ConditionalEventsFilters,
    'moreThanValue' | 'duration' | 'resolve' | 'lessThanValue'
  > = {
    duration: 0,
    resolve: 0,
  };

  switch (filters.eventType) {
    case ConditionalEventType.STOP_TIME:
      eventTypeFilters = {
        duration:
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore POST-REVIEW
          EVENT_TYPE_FILTERS.STOP_TIME.duration?.marks.find(
            (mark: { value: number }) =>
              mark.value === filters.eventTypeFilters.duration,
          )?.valueInSeconds ?? 0,

        resolve:
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore POST-REVIEW
          EVENT_TYPE_FILTERS.STOP_TIME.resolve?.marks.find(
            (mark: { value: number }) =>
              mark.value === filters.eventTypeFilters.resolve,
          )?.valueInSeconds ?? 0,
      };
      break;

    case ConditionalEventType.OVER_SPEEDING:
      eventTypeFilters = {
        moreThanValue: filters.eventTypeFilters.moreThanValue,
        resolve:
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore POST-REVIEW
          EVENT_TYPE_FILTERS.OVER_SPEEDING.resolve?.marks?.find(
            (mark: { value: number }) =>
              mark.value === filters.eventTypeFilters.resolve,
          )?.valueInSeconds ?? 0,
        duration:
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore POST-REVIEW
          EVENT_TYPE_FILTERS.OVER_SPEEDING.duration?.marks?.find(
            (mark: { value: number }) =>
              mark.value === filters.eventTypeFilters.duration,
          )?.valueInSeconds ?? 0,
      };
      break;
    // add other event types here
    default:
  }

  return {
    transports: filters.transports,
    eventType: filters.eventType,
    start: formatReportPayloadDate(filters.start),
    end: formatReportPayloadDate(filters.end),
    tz: getTimezone(),
    ...eventTypeFilters,
  };
};

export const getEventTypeFiltersInitialState = (
  searchParams: URLSearchParams,
) => {
  const eventType = searchParams.get(
    'eventType',
  ) as ConditionalEventType | null;
  const initialState = {} as ConditionalEventsFiltersState['eventTypeFilters'];

  if (!eventType || !EVENT_TYPE_FILTERS[eventType]) {
    return initialState;
  }

  return Object.values(EVENT_TYPE_FILTERS[eventType]).reduce((acc, filter) => {
    acc[filter.filterKey] = Number(searchParams?.get(filter.filterKey)) || 0;
    return acc;
  }, initialState);
};

export const getEventTypeFiltersDefaultState = (
  currentEventType?: ConditionalEventsFiltersState['eventType'],
) => {
  const defaultState = {} as ConditionalEventsFiltersState['eventTypeFilters'];

  if (currentEventType) {
    return Object.values(EVENT_TYPE_FILTERS[currentEventType]).reduce(
      (acc, filter) => {
        acc[filter.filterKey] = filter.defaultValue;
        return acc;
      },
      defaultState,
    );
  }

  return defaultState;
};

export const formatEventStartEndTime = (start: string, end: string) => {
  const startTime = format(new Date(start), 'HH:mm:ss');
  const endTime = format(new Date(end), 'HH:mm:ss');

  return `${startTime} - ${endTime}`;
};

export const formatEventDate = (date: string) => {
  return format(new Date(date), 'dd MMM, yyyy');
};
